// web/src/tutorial/screenshot-viewer.ts
// 右下角悬浮截图窗口：插件手册页专用。
//
// 为什么需要它：插件面板截图有的很长（一屏放不下），有的又很宽，塞进正文会把
// 阅读节奏打碎。所以做成固定悬浮窗口：
//   · **整张截图原样显示**：不裁剪、不留空边、画面本身不移动；
//   · 窗口尺寸按图的整图比例算，图正好铺满窗口（横图窗口宽、竖图窗口高）；
//   · **多图页面跟着正文走**：作者在正文 section 上标了 data-shot（图片下标），
//     滚到哪一节就自动换成那张截图 —— 读者不必先发现「窗口上方有页签」；
//     页签仍然保留，点了可以当场看另一张，滚到别的节会再自动跟回去；
//   · 可以收起成一个小按钮，不挡内容；
//   · 图随站点语言自动切换（中文站用中文界面截图，英文站用英文界面截图）。
//
// 历史：曾做过「滚动跟随 + 按分区裁剪放大某一块」，实测无法每次都刚好落在用户
// 正在读的那一块上（还要压暗高亮），观感更差，裁剪机制已整体删除。现在跟随的粒度
// 只到「换哪张图」，画面本身始终完整、静止。

import { getLang } from "../i18n";
import { pickText } from "../site-data";
import { escapeHtml } from "./render";
import type { DocImage, TutorialDoc } from "./types";

const STORAGE_COLLAPSED = "md-shot-collapsed";
const STORAGE_LANG_OVERRIDE = "md-shot-lang-override";
const STORAGE_MAXIMIZED = "md-shot-maximized";

/**
 * 窗口尺寸按当前这张图的**整图比例**算，分两种显示状态：
 *
 * **默认（收着）**：宽度上限 `VIEWPORT_DEFAULT_W = 820`，高度用到视口高。
 *   图放得下就按整图比例完整显示；放不下（Rust 端截图字小，缩下去看不清）
 *   就按原生像素渲染 + 图窗内滚动。默认不占太多正文区域。
 *
 * **放大（点标题栏的「放大」按钮）**：宽度上限提到 `VIEWPORT_MAX_W = 1200`
 *   （再宽在常见屏幕上也没意义），高度上限 `VIEWPORT_MAX_H = 1400` 基本不设限，
 *   允许窗口盖住正文、需要时在图窗里滚动查看细节。状态记在 localStorage。
 *
 * 尺寸只受「屏幕」约束，不受正文宽度约束；两种状态的上沿都不会越过吸顶导航条，
 * 所以右上角的 ✕ 收起按钮永远点得到。
 *
 * 历史：2026-09-25 曾一度把默认宽度也放开到 1200（作者反馈截图太宽看不全），
 * 随后作者确认「默认还是要有宽度限制，另外给一个最大化按钮」——即现在这套。
 */
const VIEWPORT_DEFAULT_W = 820;
const VIEWPORT_MAX_W = 1200;
const VIEWPORT_MAX_H = 1400;
const VIEWPORT_MIN_W = 240;
const VIEWPORT_MIN_H = 220;
const BODY_PAD = 8;
/** 悬浮窗与视口边缘的间距（与 CSS .shot-viewer 的 right / bottom 一致） */
const ANCHOR_GAP = 18;
/** 顶部留给吸顶导航条的高度（超出这个范围上沿就会被导航条压住） */
const TOP_RESERVE = 88;

/**
 * 判定「当前读到哪一节」的检测线：视口顶部往下 30% 处。
 * 比视口中线略靠上，这样标题一进入阅读区中部就切图，切换不会显得滞后。
 */
const ACTIVE_LINE_RATIO = 0.3;

type ShotLang = "zh" | "en";

interface Entry {
  img: DocImage;
}

/** 正文里带 data-shot 的区块：滚到它就切到 entry */
interface ShotHint {
  el: HTMLElement;
  entry: Entry;
}

const LABEL = {
  title: { zh: "插件界面", en: "Plugin UI" },
  collapse: { zh: "收起截图", en: "Hide screenshot" },
  expand: { zh: "展开截图", en: "Show screenshot" },
  empty: { zh: "这一页没有配插件界面截图", en: "No plugin screenshot for this page" },
  hint: {
    zh: "看图会跟着正文自动切换；挡住正文就点右上角 ✕ 收起",
    en: "The screenshot follows the section you read — click ✕ if it covers the text",
  },
  /** 图比屏幕能显示的更大，按原生像素渲染，需要读者在图窗里滚动 */
  hintScroll: {
    zh: "已放大：这张图按原尺寸显示，看不全的部分在图里滚动查看「还原」可回到缩放显示",
    en: "Maximised: shown at full size — scroll inside the image for the rest, or click “Restore” to scale it back down",
  },
  hintStatic: {
    zh: "挡住正文就点右上角 ✕ 收起，收起状态会被记住",
    en: "Click ✕ to hide it — your choice is remembered",
  },
  hintMax: {
    zh: "已放大：窗口可以盖住正文，图需要滚动就滚一下；点「还原」回到缩放显示",
    en: "Maximised — the panel may cover the text; scroll inside the image if needed. Click “Restore” to scale it back down",
  },
  maximize: { zh: "放大", en: "Maximise" },
  restore: { zh: "还原", en: "Restore" },
  langToggle: { zh: "看原文界面", en: "Localized UI" },
};

function srcFor(img: DocImage, lang: ShotLang): string {
  if (lang === "en" && img.srcEn) return img.srcEn;
  return img.src;
}

/** 该图在这门语言下是否有独立截图（没有就不显示语言小开关） */
function hasBothLangs(images: Entry[]): boolean {
  return images.some((e) => !!e.img.srcEn && e.img.srcEn !== e.img.src);
}

/**
 * 图片的真实像素尺寸：优先用浏览器量到的（EN 截图与 CN 截图的宽高常常不一样，
 * 内容里声明的 width/height 只当图还没加载出来时的兜底）。
 */
function measuredSize(
  img: HTMLImageElement,
  fallback: DocImage,
): { w: number; h: number } {
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    return { w: img.naturalWidth, h: img.naturalHeight };
  }
  return { w: fallback.width, h: fallback.height };
}

export function mountScreenshotViewer(doc: TutorialDoc): void {
  const images = (doc.images || []).filter((i) => !!i.src);
  const existing = document.querySelector<HTMLElement>("[data-shot-viewer]");
  if (images.length === 0) {
    existing?.remove();
    return;
  }

  const entries: Entry[] = images.map((img) => ({ img }));

  // 正文里声明的「这一节讲哪张图」：按 data-shot 找 images 下标（也接受图片标签）
  const hints: ShotHint[] = [];
  document
    .querySelectorAll<HTMLElement>("[data-shot]")
    .forEach((el) => {
      const raw = el.dataset.shot ?? "";
      let entry: Entry | undefined;
      if (/^\d+$/.test(raw)) entry = entries[Number(raw)];
      else entry = entries.find((e) => e.img.label?.en === raw || e.img.label?.zh === raw);
      if (entry) hints.push({ el, entry });
    });

  // ── DOM ────────────────────────────────────────────────────
  const root = existing || document.createElement("aside");
  if (!existing) {
    root.className = "shot-viewer";
    root.setAttribute("data-shot-viewer", "");
    root.setAttribute("aria-label", "插件界面截图");
    document.body.appendChild(root);
  }
  root.dataset.shotCount = String(entries.length);
  root.dataset.hints = String(hints.length);

  let langOverride = readOverride();
  let current: Entry | null = null;
  let visible = false;
  /** 放大状态：去掉默认的宽度上限，窗口可以盖住正文（点标题栏按钮切换） */
  let maximized = readMaximized();
  root.dataset.maximized = maximized ? "1" : "0";
  /** 读者手动点过页签：在还没滚到任何「有标注的节」之前，尊重他的选择 */
  let manual = false;

  function readMaximized(): boolean {
    try {
      return localStorage.getItem(STORAGE_MAXIMIZED) === "1";
    } catch {
      return false;
    }
  }

  function writeMaximized(value: boolean): void {
    try {
      if (value) localStorage.setItem(STORAGE_MAXIMIZED, "1");
      else localStorage.removeItem(STORAGE_MAXIMIZED);
    } catch {
      /* 隐私模式下忽略 */
    }
  }

  function readOverride(): ShotLang | null {
    try {
      const v = localStorage.getItem(STORAGE_LANG_OVERRIDE);
      return v === "zh" || v === "en" ? v : null;
    } catch {
      return null;
    }
  }

  function writeOverride(v: ShotLang | null): void {
    try {
      if (v) localStorage.setItem(STORAGE_LANG_OVERRIDE, v);
      else localStorage.removeItem(STORAGE_LANG_OVERRIDE);
    } catch {
      /* 隐私模式下忽略 */
    }
  }

  function shotLang(): ShotLang {
    return langOverride || (getLang() === "zh" ? "zh" : "en");
  }

  function isCollapsed(): boolean {
    try {
      return localStorage.getItem(STORAGE_COLLAPSED) === "1";
    } catch {
      return false;
    }
  }

  function setCollapsed(v: boolean): void {
    try {
      localStorage.setItem(STORAGE_COLLAPSED, v ? "1" : "0");
    } catch {
      /* 忽略 */
    }
    root.classList.toggle("is-collapsed", v);
    root.dataset.collapsed = v ? "1" : "0";
  }

  function render(): void {
    const lang = getLang();
    const ui = lang === "zh";
    const sl = shotLang();
    const multiple = entries.length > 1;
    const tabs = multiple
      ? `<div class="shot-tabs">${entries
          .map((e, i) => {
            const name = e.img.label
              ? pickText(e.img.label, lang)
              : `${i + 1}`;
            return `<button type="button" class="shot-tab${
              current === e ? " is-active" : ""
            }" data-shot-tab="${i}">${escapeHtml(name)}</button>`;
          })
          .join("")}</div>`
      : "";
    const langToggle = hasBothLangs(entries)
      ? `<button type="button" class="shot-lang" data-shot-lang title="${escapeHtml(
          ui ? LABEL.langToggle.zh : LABEL.langToggle.en,
        )}">${sl === "zh" ? "中文界面" : "EN UI"}</button>`
      : "";
    const note = current
      ? escapeHtml(
          current.img.label
            ? pickText(current.img.label, lang)
            : LABEL.title[lang],
        )
      : escapeHtml(LABEL.empty[lang]);
    /*
     * 页脚提示：默认档永远是缩放适应（画面完整），提示只说「会跟着正文切换」；
     * 放大档的文案由 layoutAndApply() 里的 syncFootHint() 按实际档位刷新。
     */
    const hintText =
      entries.length > 1 && hints.length > 0 ? LABEL.hint.zh : LABEL.hintStatic.zh;
    const hintTextEn =
      entries.length > 1 && hints.length > 0
        ? LABEL.hint.en
        : LABEL.hintStatic.en;
    const maximizeBtn = `<button type="button" class="shot-icon-btn shot-max-btn" data-shot-maximize title="${escapeHtml(
      ui
        ? maximized
          ? LABEL.restore.zh
          : LABEL.maximize.zh
        : maximized
          ? LABEL.restore.en
          : LABEL.maximize.en,
    )}" aria-pressed="${maximized ? "true" : "false"}">${escapeHtml(
      ui ? (maximized ? LABEL.restore.zh : LABEL.maximize.zh) : maximized ? LABEL.restore.en : LABEL.maximize.en,
    )}</button>`;

    root.innerHTML = `
<button type="button" class="shot-fab" data-shot-fab title="${escapeHtml(
      ui ? LABEL.expand.zh : LABEL.expand.en,
    )}">
  <span class="shot-fab-icon" aria-hidden="true">🖼</span>
  <span class="shot-fab-text">${escapeHtml(ui ? LABEL.title.zh : LABEL.title.en)}</span>
</button>
<div class="shot-shell">
  <div class="shot-head">
    <span class="shot-head-title">${escapeHtml(
      ui ? LABEL.title.zh : LABEL.title.en,
    )}</span>
    ${langToggle}
    ${maximizeBtn}
    <button type="button" class="shot-icon-btn" data-shot-collapse title="${escapeHtml(
      ui ? LABEL.collapse.zh : LABEL.collapse.en,
    )}" aria-label="${escapeHtml(ui ? LABEL.collapse.zh : LABEL.collapse.en)}">✕</button>
  </div>
  ${tabs}
  <div class="shot-body" data-shot-body>
    <div class="shot-viewport" data-shot-viewport>
      <img class="shot-img" data-shot-img alt="${escapeHtml(note)}" decoding="async" />
    </div>
  </div>
  <div class="shot-foot">
    <span class="shot-caption" data-shot-caption>${note}</span>
    <span class="shot-hint">${escapeHtml(ui ? hintText : hintTextEn)}</span>
  </div>
</div>`;

    if (visible) {
      layoutAndApply();
      // 首帧也要把「现在看的是第几张」标出来
      highlightTabs();
      bindImageLoad();
    }
  }

  /**
   * 窗口里「除图窗之外」要多占的**高度**（页头 + 页签 + 页脚 + 边框 + 内层留白）。
   * 必须实测，不能猜：如果高度上限只按图窗算，实际窗口会多出那一截，上沿跑到屏幕外，
   * ✕ 收起按钮就点不到了。缓存实测值，只在发现不够用时重算。
   */
  let chromeH = 160;

  function syncChrome(): void {
    const shell = root.querySelector<HTMLElement>(".shot-shell");
    const body = root.querySelector<HTMLElement>("[data-shot-body]");
    if (!shell || !body) return;
    const measuredH = shell.clientHeight - body.clientHeight;
    if (measuredH > chromeH) chromeH = measuredH;
  }

  /**
   * 窗口尺寸按当前这张图的**整图比例**算，分两档（由「放大」决定，不看图片大小）：
   *
   *   · 默认 / 还原：**缩放适应** —— 按整图比例缩到窗口内，画面完整、不截断，
   *     宽度上限 `VIEWPORT_DEFAULT_W`（克制，不占太多正文）。
   *   · 放大：**按图的原生像素显示**，窗口上限放宽到 `VIEWPORT_MAX_W × VIEWPORT_MAX_H`
   *     （基本不设限），允许盖住正文；图比窗口大时在图窗里滚动看细节，
   *     图比窗口小时保持原尺寸、不放大。
   */
  function computeLayout(
    img: DocImage,
    size: { w: number; h: number },
  ): {
    bodyW: number;
    bodyH: number;
    vpW: number;
    vpH: number;
    /** fit = 缩放适应图窗（完整显示）；native = 原生像素 + 图窗内滚动 */
    mode: "fit" | "native";
  } | null {
    const body0 = root.querySelector<HTMLElement>("[data-shot-body]");
    if (!body0) return null;
    syncChrome();
    const widthCap = maximized ? VIEWPORT_MAX_W : VIEWPORT_DEFAULT_W;
    // 可用宽度 = min(本档宽度上限, 视口宽 − 左右贴边)。
    const availW = Math.max(
      VIEWPORT_MIN_W,
      Math.min(widthCap, window.innerWidth - 2 * ANCHOR_GAP) - 2 * BODY_PAD,
    );
    // 可用高度 = 视口高 − 顶部导航条留白 − 底部贴边 − 窗口自身的页头/页签/页脚开销。
    const availH = Math.max(
      VIEWPORT_MIN_H,
      Math.min(
        VIEWPORT_MAX_H,
        window.innerHeight - TOP_RESERVE - ANCHOR_GAP - chromeH,
      ) - 2 * BODY_PAD,
    );
    const aspect = size.w / size.h;

    // 默认档：整图按比例缩到可用框内（contain），永远完整显示、不截断。
    let vpW = availW;
    let vpH = Math.round(vpW / aspect);
    if (vpH > availH) {
      vpH = availH;
      vpW = Math.round(vpH * aspect);
    }

    // 放大档：按原生像素显示；图放不下时图窗可滚动，放得下就保持原尺寸。
    // 注意：只放大/缩小「图窗」，图片本身在 apply() 里按 1:1 渲染。
    let mode: "fit" | "native" = "fit";
    if (maximized) {
      mode = "native";
      vpW = Math.max(VIEWPORT_MIN_W, Math.min(size.w, availW));
      vpH = Math.max(VIEWPORT_MIN_H, Math.min(size.h, availH));
    }
    vpH = Math.max(VIEWPORT_MIN_H, Math.min(vpH, availH));
    vpW = Math.max(80, Math.min(vpW, availW));
    const pad = 2 * BODY_PAD;
    return { bodyW: vpW + pad, bodyH: vpH + pad, vpW, vpH, mode };
  }

  /** 算窗口尺寸并把图铺满（整张图，不裁剪） */
  function layoutAndApply(): void {
    if (!current) return;
    const img = root.querySelector<HTMLImageElement>("[data-shot-img]");
    const body = root.querySelector<HTMLElement>("[data-shot-body]");
    if (!img || !body) return;
    const size = measuredSize(img, current.img);
    const l = computeLayout(current.img, size);
    if (l) {
      const w = `${l.bodyW}px`;
      const h = `${l.bodyH}px`;
      if (body.style.width !== w || body.style.height !== h) {
        body.style.width = w;
        body.style.height = h;
      }
      root.dataset.aspect = `${size.w}x${size.h}`;
    }
    // 图片怎么渲染由 computeLayout 决定的 mode 说了算，避免「窗口按 A 档、图片按 B 档」打架
    apply(l ? l.mode : maximized ? "native" : "fit");
    applyAnchor();
    // 页脚提示随「缩放适应 / 原尺寸」变化，放在摆完之后同步
    syncFootHint();
    // 页签高亮 / 「现在看第几张」始终与当前显示的图保持一致
    highlightTabs();
  }

  /** 页脚提示文案：默认档说「跟着正文切换」，放大档说「已放大 / 可滚动」 */
  function syncFootHint(): void {
    const hint = root.querySelector<HTMLElement>(".shot-hint");
    if (!hint) return;
    const ui = getLang() === "zh";
    const native = root.dataset.shown === "native";
    const text = maximized
      ? native
        ? ui
          ? LABEL.hintScroll.zh
          : LABEL.hintScroll.en
        : ui
          ? LABEL.hintMax.zh
          : LABEL.hintMax.en
      : entries.length > 1 && hints.length > 0
        ? ui
          ? LABEL.hint.zh
          : LABEL.hint.en
        : ui
          ? LABEL.hintStatic.zh
          : LABEL.hintStatic.en;
    if (hint.textContent !== text) hint.textContent = text;
  }

  /**
   * 图解码完成后重新摆一次窗口：先按（已量到的）真实像素算窗口尺寸，
   * 再把窗口对齐真实比例 —— 中英两版截图的窗口大小常常不同，切语言时靠这一步。
   */
  function relayout(): void {
    layoutAndApply();
    fitToImage();
  }

  /**
   * 让窗口自己决定往上占多高：贴底放不下时就把 bottom 偏移加大，窗口往屏幕上方长，
   * 而**不是**把图压小。窗口再高也不会高过「视口 − 顶部导航条留白」，
   * 所以右上角的 ✕ 收起按钮永远点得到。
   */
  function applyAnchor(): void {
    const shell = root.querySelector<HTMLElement>(".shot-shell");
    if (!shell) return;
    const shellHeight = shell.getBoundingClientRect().height;
    if (shellHeight <= 0) return;
    root.dataset.shellH = String(Math.round(shellHeight));
    const maxAllowed = Math.max(0, window.innerHeight - TOP_RESERVE - ANCHOR_GAP);
    const bottom =
      shellHeight > maxAllowed
        ? window.innerHeight - shellHeight - TOP_RESERVE
        : ANCHOR_GAP;
    root.style.bottom = `${Math.max(Math.round(bottom), 4)}px`;
  }

  /**
   * 窗口尺寸对齐「当前这张图」的真实像素比例。
   *
   * 为什么需要它：中英两版截图的**窗口大小常常不同**（例如同一页中文是
   * 1920×1040 的全屏截图、英文是 902×875 的小窗截图），内容里声明的
   * width/height 取的是两版上限。若只按上限算一次窗口尺寸，切到另一种语言时
   * 比例对不上，图会被拉伸变形（`verify:shots` 会断言「渲染比例 == 原图比例」）。
   * 所以图解码完成后（以及切语言换图后）都按真实像素重算一次窗口。
   */
  function fitToImage(): void {
    if (!visible) return;
    const img = root.querySelector<HTMLImageElement>("[data-shot-img]");
    const body = root.querySelector<HTMLElement>("[data-shot-body]");
    if (!img || !body || !img.naturalWidth || !img.naturalHeight) return;
    const aspect = img.naturalWidth / img.naturalHeight;
    const bodyW = body.getBoundingClientRect().width;
    const bodyH = body.getBoundingClientRect().height;
    if (bodyW > 0 && bodyH > 0) {
      const currentAspect = bodyW / bodyH;
      // 比例已经一致就不动，避免每帧重写样式
      if (Math.abs(currentAspect - aspect) / aspect < 0.005) return;
    }
    layoutAndApply();
  }

  /**
   * 依据当前 entry 设置 img，并按 `mode` 决定怎么放：
   *
   *   `fit`    —— 缩放适应：整图按比例缩放铺满图窗，画面完整、不截断（默认档）。
   *   `native` —— 原生像素 1:1 渲染，超出图窗的部分靠图窗内滚动查看（放大档）。
   *
   * `mode` 由 computeLayout() 决定（它同时决定图窗尺寸），保证「窗口尺寸」与
   * 「图片怎么放」永远一致；切图 / 切语言时把滚动位置复位到左上角。
   */
  function apply(mode: "fit" | "native"): void {
    const viewport = root.querySelector<HTMLElement>("[data-shot-viewport]");
    const img = root.querySelector<HTMLImageElement>("[data-shot-img]");
    const caption = root.querySelector<HTMLElement>("[data-shot-caption]");
    if (!viewport || !img || !current) return;

    const entry = current;
    const src = srcFor(entry.img, shotLang());
    if (img.getAttribute("src") !== src) {
      img.setAttribute("src", src);
      // 新图解码完成后才知道真实尺寸（中英两版宽高常不同），届时重新算一次窗口比例
      bindImageLoad();
    }
    const captionText = entry.img.label
      ? pickText(entry.img.label, getLang())
      : LABEL.title[getLang()];
    if (caption && caption.textContent !== captionText) {
      caption.textContent = captionText;
    }
    img.alt = captionText;

    const size = measuredSize(img, entry.img);
    const vw = viewport.clientWidth || VIEWPORT_MIN_W;
    const vh = viewport.clientHeight || VIEWPORT_MIN_H;
    if (mode === "native") {
      img.style.width = `${size.w}px`;
      img.style.height = `${size.h}px`;
    } else {
      const scale = Math.max(vw / size.w, vh / size.h);
      img.style.width = `${Math.round(size.w * scale)}px`;
      img.style.height = `${Math.round(size.h * scale)}px`;
    }
    // CSS 只负责「能不能滚」：原生档给滚动条，缩放档锁死
    viewport.classList.toggle("is-native", mode === "native");
    root.dataset.shown = mode;
    viewport.scrollTop = 0;
    viewport.scrollLeft = 0;
    img.style.transform = "";
  }

  function show(v: boolean): void {
    visible = v;
    root.classList.toggle("is-visible", v);
    if (v) {
      if (!current) current = entries[0];
      syncFromScroll();
      relayout();
      bindImageLoad();
    }
  }

  /**
   * 按阅读位置挑出「这一节讲的是哪张图」，与当前显示的不同才切换。
   * 只换图、不改画面位置：每张图都是整张等比铺满的，切过去就是一张完整截图。
   */
  function pickHint(): ShotHint | null {
    const line = window.innerHeight * ACTIVE_LINE_RATIO;
    let picked: ShotHint | null = null;
    for (const hint of hints) {
      if (hint.el.getBoundingClientRect().top <= line) picked = hint;
      else break;
    }
    return picked;
  }

  function syncFromScroll(): void {
    const picked = pickHint();
    if (!picked) {
      // 还没滚到任何有标注的节：只在读者没手动选过的时候回到第一张
      if (!manual && entries.length > 1 && current !== entries[0]) {
        current = entries[0];
        relayout();
        highlightTabs();
      }
      return;
    }
    if (picked.entry === current) return;
    manual = false;
    current = picked.entry;
    relayout();
    // 页签高亮跟着走，读者能看出「一共几张、现在看第几张」
    highlightTabs();
  }

  function highlightTabs(): void {
    const active = current ? entries.indexOf(current) : -1;
    root.dataset.shotIndex = String(active);
    root.querySelectorAll<HTMLElement>("[data-shot-tab]").forEach((btn) => {
      btn.classList.toggle("is-active", Number(btn.dataset.shotTab) === active);
    });
  }

  /**
   * 截图真正解码完成后才知道它的像素尺寸（EN 与 CN 截图的窗口大小常常不同），
   * 那时按真实尺寸重算一次窗口；同一个 <img> 上只挂一次（render() 重建 DOM 后会重新挂）。
   */
  function bindImageLoad(): void {
    const img = root.querySelector<HTMLImageElement>("[data-shot-img]");
    if (!img) return;
    const src = img.getAttribute("src") || "";
    if (img.dataset.boundSrc === src) return;
    img.dataset.boundSrc = src;
    img.addEventListener("load", () => {
      if (!visible || img.getAttribute("src") !== src) return;
      relayout();
    });
    // 图已在缓存里时 load 不会再触发，这里补一次
    if (img.complete && img.naturalWidth > 0) relayout();
  }

  // ── 事件 ───────────────────────────────────────────────────
  root.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    if (target.closest("[data-shot-collapse]")) {
      setCollapsed(true);
      show(false);
      return;
    }
    if (target.closest("[data-shot-fab]")) {
      setCollapsed(false);
      show(true);
      return;
    }
    const tab = target.closest<HTMLElement>("[data-shot-tab]");
    if (tab) {
      const i = Number(tab.dataset.shotTab);
      const entry = entries[i];
      if (!entry) return;
      // 手动点页签：当场切过去看；继续往下滚到别的节，会再自动跟回去
      current = entry;
      manual = true;
      render();
      return;
    }
    const langBtn = target.closest("[data-shot-lang]");
    if (langBtn) {
      langOverride = shotLang() === "zh" ? "en" : "zh";
      writeOverride(langOverride);
      render();
      return;
    }
    // 放大 / 还原：切换默认宽度上限，整窗按新上限重算
    const maxBtn = target.closest("[data-shot-maximize]");
    if (maxBtn) {
      maximized = !maximized;
      writeMaximized(maximized);
      root.dataset.maximized = maximized ? "1" : "0";
      render();
    }
  });

  // 滚动：rAF 合并；只在「该换图了」的时候动 DOM
  let raf = 0;
  window.addEventListener(
    "scroll",
    () => {
      if (!visible || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        syncFromScroll();
      });
    },
    { passive: true },
  );
  window.addEventListener("resize", () => {
    if (visible) layoutAndApply();
  });
  // 语言切换：整页重绘后正文 data-shot 不变，但截图要换语言
  document.addEventListener("langchange", () => {
    render();
    syncFromScroll();
    relayout();
  });

  // ── 初始化 ─────────────────────────────────────────────────
  const narrow = window.matchMedia("(max-width: 900px)");
  function applyViewport(): void {
    if (narrow.matches) {
      show(false);
      root.hidden = true;
      return;
    }
    root.hidden = false;
    const collapsed = isCollapsed();
    root.classList.toggle("is-collapsed", collapsed);
    // 先把要显示的图定下来，再渲染：否则首帧页签没有高亮、图也没 src
    if (!current) current = entries[0];
    syncFromScroll();
    render();
    if (!collapsed) {
      show(true);
    } else {
      visible = false;
      root.classList.remove("is-visible");
      layoutAndApply();
    }
  }

  narrow.addEventListener("change", applyViewport);
  applyViewport();
}
